import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';
import './Reminders.css';

const API_BASE = '/api';

const SOURCE_LABELS = {
  user_request: 'Вручную',
  calendar_create: 'Событие',
  calendar_move: 'Событие',
  financial: 'Финансы',
};

const DATE_TIME_OPTIONS = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/** Локальная дата YYYY-MM-DD (без UTC-сдвига). */
function localYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addOneCalendarMonth(base = new Date()) {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setMonth(d.getMonth() + 1);
  return d;
}

function formatDueAt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', DATE_TIME_OPTIONS);
}

function getSourceLabel(source) {
  return SOURCE_LABELS[source] || source || '—';
}

/** Текст правила напоминания: 70%, 30%, 2h, 5% для событий; для финансов — название правила и вид (3d, 2d, due и т.д.). */
function getRuleLabel(r) {
  const kind = r.reminder_kind || '';
  const name = r.rule_name || '';
  if (name && kind) return `${name} (${kind})`;
  if (kind) return kind;
  if (name) return name;
  return null;
}

/** Группировка напоминаний: по событию (calendar_event_id) и отдельно "прочие". */
function groupReminders(reminders) {
  const now = new Date();
  const today = localYmd(now);
  const groups = [
    {key:'overdue',label:'Требуют внимания',items:[]},
    {key:'today',label:'Сегодня',items:[]},
    {key:'later',label:'Позже',items:[]},
    {key:'history',label:'История отправки',items:[]},
  ];
  reminders.forEach(r => {
    const due = new Date(r.due_at);
    const index = r.sent_at ? 3 : localYmd(due) === today ? 1 : due < now ? 0 : 2;
    groups[index].items.push(r);
  });
  groups.forEach(g => g.items.sort((a,b) => g.key === 'history' ? new Date(b.due_at)-new Date(a.due_at) : new Date(a.due_at)-new Date(b.due_at)));
  return groups.filter(g => g.items.length);
}

function reminderMessageAside(r) {
  const text = (r.rendered_message || '').trim();
  return text || 'Текст появится перед отправкой';
}

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => localYmd(new Date()));
  const [dateTo, setDateTo] = useState(() => localYmd(addOneCalendarMonth(new Date())));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        start_date: dateFrom,
        end_date: dateTo,
        include_sent: '1',
      });
      const res = await apiFetch(`${API_BASE}/reminders/?${params.toString()}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Ошибка ${res.status}`);
      }
      const data = await res.json();
      setReminders(data.reminders || []);
    } catch (e) {
      setError(e.message || 'Не удалось загрузить напоминания');
      setReminders([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="Reminders">
      <div className="Reminders-toolbar">
        <h2 className="Reminders-title">Напоминания</h2>
        <div className="Reminders-period">
          <label className="Reminders-periodLabel">
            С
            <input
              type="date"
              className="Reminders-periodInput"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className="Reminders-periodLabel">
            По
            <input
              type="date"
              className="Reminders-periodInput"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
        </div>
        <button type="button" className="Reminders-btn" onClick={load} disabled={loading}>
          Обновить
        </button>
      </div>
      {error && (
        <div className="Reminders-error">{error}</div>
      )}
      {loading && reminders.length === 0 && !error && (
        <div className="Reminders-loading">Загрузка…</div>
      )}
      {!error && !loading && reminders.length === 0 && (
        <div className="Reminders-empty">
          Нет напоминаний в выбранном периоде ({dateFrom} — {dateTo}).
        </div>
      )}
      {!error && reminders.length > 0 && (
        <div className="Reminders-list">
          {groupReminders(reminders).map((group) => (
            <section key={group.key} className="Reminders-group">
              <div className="Reminders-groupHeader">
                {group.label} · {group.items.length}
              </div>
              <ul className="Reminders-groupList">
                {group.items.map((r) => (
                  <li key={r.id} className="Reminders-item">
                    <div className="Reminders-itemMain">
                      {r.event_title && <strong className="Reminders-eventTitle">{r.event_title}</strong>}
                      <div className="Reminders-itemTimeRow">
                        <span className="Reminders-itemDue">
                          Напоминание в: <strong>{formatDueAt(r.due_at)}</strong>
                        </span>
                        <span
                          className={
                            r.sent_at
                              ? 'Reminders-status Reminders-status--sent'
                              : 'Reminders-status Reminders-status--queued'
                          }
                        >
                          {r.sent_at ? 'Отправлено' : 'В очереди'}
                        </span>
                      </div>
                      {r.sent_at && (
                        <div className="Reminders-itemSentDetail">
                          Время отправки: {formatDueAt(r.sent_at)}
                        </div>
                      )}
                      {r.content && (
                        <div className="Reminders-itemContent">{r.content}</div>
                      )}
                      <div className="Reminders-itemMeta">
                        <span className="Reminders-itemSource">{getSourceLabel(r.source)}</span>
                        {getRuleLabel(r) && (
                          <span className="Reminders-itemRule"> · Правило: {getRuleLabel(r)}</span>
                        )}
                      </div>
                    </div>
                    <aside
                      className={
                        (r.rendered_message || '').trim()
                          ? 'Reminders-itemAside'
                          : 'Reminders-itemAside Reminders-itemAside--placeholder'
                      }
                    >
                      {reminderMessageAside(r)}
                    </aside>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
